import React, { useCallback, useState } from 'react';
import { useSourcingResults } from 'features/ai-assistant/hooks';
import { useUserContext } from 'providers/UserProvider';
import { SECONDARY_API_CLIENT } from 'services/API';
import { When } from 'UI/components/atoms';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';

import { AIProspectingResultsTable } from '../AIProspectingResultsTable';
import { Conversation } from '../Conversation';
import { MessageMetaData } from '../MessageMetaData';

import {
  AI_PROSPECTING_ASSISTANT_BASE_PATH,
  COPIES
} from './AIProspectingAssistantDrawer.constants';
import { useDrawerStyles } from './AIProspectingAssistantDrawer.styled';

export const AIProspectingAssistantDrawer = ({ entityId, initialSearchText, open, onClose }) => {
  const classes = useDrawerStyles();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [user] = useUserContext();
  const sessionId = `session-${user?.id}-${entityId}`;

  const {
    activeResponseId,
    entityType,
    handleMessageReplied,
    handleMessagesLoaded,
    handleMetaDataClick
  } = useSourcingResults();

  const metaDataComponent = useCallback(
    ({ data }) => (
      <MessageMetaData
        entityType={entityType}
        message={data}
        onClick={handleMetaDataClick}
        isActive={data?.id === activeResponseId}
      />
    ),
    [activeResponseId, entityType, handleMetaDataClick]
  );

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick') return;
    setIsConfirmationDialogOpen(true);
  };

  const handleConfirmDrawerClose = () => {
    setIsConfirmationDialogOpen(false);
    onClose && onClose();
  };

  const closeDialogActions = [
    {
      variant: 'outlined',
      text: COPIES.warningCloseDialog.actions.discardAndExit,
      onClick: handleConfirmDrawerClose
    },
    {
      text: COPIES.warningCloseDialog.actions.cancel,
      onClick: () => setIsConfirmationDialogOpen(false)
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{
        title: COPIES.title
      }}
      FPDrawerProps={{
        open,
        classes: { paper: classes.drawerLarge },
        ModalProps: { disableEscapeKeyDown: true }
      }}
      onClose={handleClose}
      hasPaddingContent={false}
      hideFooter
    >
      <div className={classes.dualContainers}>
        <div className={classes.aiAssistantContainer}>
          <Conversation
            apiInstance={SECONDARY_API_CLIENT}
            basePath={AI_PROSPECTING_ASSISTANT_BASE_PATH}
            canSelectAIModels={false}
            composerPlaceholder={COPIES.composerPlaceholder}
            initialSearchText={initialSearchText}
            messageFullWidth
            metaDataComponent={metaDataComponent}
            onMessageReplied={handleMessageReplied}
            onMessagesLoaded={handleMessagesLoaded}
            sessionId={sessionId}
          />
        </div>

        <div className={classes.rightContainer}>
          <AIProspectingResultsTable
            key={`${entityType}-${activeResponseId}`}
            entityType={entityType}
            responseId={activeResponseId}
          />
        </div>
      </div>

      <When condition={isConfirmationDialogOpen}>
        <DecisionDialog
          actions={closeDialogActions}
          isOpened
          message={COPIES.warningCloseDialog.message}
          onClose={() => setIsConfirmationDialogOpen(false)}
          showSeverityIcon
          title={COPIES.warningCloseDialog.title}
        />
      </When>
    </FPDrawer>
  );
};
